import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Json, TablesInsert } from '@/integrations/supabase/types';

export interface QuestionItem {
  text: string;
  scoring_criteria: string;
  read_time_sec: number;
  answer_time_sec: number;
  weight: number;
}

export type InterviewType = 'video_audio' | 'audio_only';

export interface InterviewConfigRow {
  id: string;
  job_id: string;
  questions_json: QuestionItem[];
  max_retakes: number | null;
  total_time_cap: number | null;
  status: string;
  interview_type: InterviewType;
  created_at: string;
  updated_at: string;
}

function questionToJson(question: QuestionItem): Json {
  return {
    text: question.text,
    scoring_criteria: question.scoring_criteria,
    read_time_sec: question.read_time_sec,
    answer_time_sec: question.answer_time_sec,
    weight: question.weight,
  };
}

export function useInterviewConfig(jobId: string | undefined) {
  return useQuery({
    queryKey: ['interview-config', jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('interview_configs')
        .select('*')
        .eq('job_id', jobId!)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as InterviewConfigRow | null;
    },
  });
}

export function useSaveInterviewConfig(onPublished?: () => void) {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      jobId,
      questions,
      status,
      interviewType,
      interviewExpiryDays,
      existingId,
    }: {
      jobId: string;
      questions: QuestionItem[];
      status: 'draft' | 'published';
      interviewType?: InterviewType;
      interviewExpiryDays?: number;
      existingId?: string;
    }) => {
      const payload: TablesInsert<'interview_configs'> = {
        job_id: jobId,
        questions_json: questions.map(questionToJson),
        status,
      };
      if (interviewType) {
        payload.interview_type = interviewType;
      }
      if (interviewExpiryDays != null) {
        payload.interview_expiry_days = interviewExpiryDays;
      }

      if (status === 'published') {
        const { data, error } = await supabase.functions.invoke('publish-interview-config', {
          body: {
            job_id: jobId,
            questions,
            interview_type: interviewType,
            interview_expiry_days: interviewExpiryDays,
            existing_id: existingId,
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return data.config;
      }

      if (existingId) {
        const { data, error } = await supabase
          .from('interview_configs')
          .update(payload)
          .eq('id', existingId)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('interview_configs')
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: async (data, vars) => {
      qc.invalidateQueries({ queryKey: ['interview-config', vars.jobId] });
      qc.invalidateQueries({ queryKey: ['job-interview-ready'] });
      toast({
        title: vars.status === 'published' ? 'Interview published successfully' : 'Draft saved',
        description:
          vars.status === 'published'
            ? 'Interview session is now ready for candidates.'
            : 'Your changes have been saved as a draft.',
      });
      if (vars.status === 'published' && onPublished) {
        onPublished();
      }
      if (vars.status === 'published') qc.invalidateQueries({ queryKey: ['interview-session-exists'] });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error saving interview config',
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });
}
